import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getEmployeesStatsCall } from '@/people/state/api';
import {
  getSurveyCampaignByIdResponsesStatsCall,
  getSurveysCampaignsCall,
  getTemplatesCall,
} from '../api';

export const useCurrentCampaign = () => {
  const [currentVersion, setCurrentVersion] = useState<{ name: string; id: number }>();
  const { surveyId } = useParams();
  const { response: responseGetTemplates } = getTemplatesCall.hook();
  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  const {
    execute: executeGetSurveyCampaignByIdResponsesStats,
    response: responseGetSurveyCampaignByIdResponsesStats,
    loading: loadingGetSurveyCampaignByIdResponsesStats,
    error: errorGetSurveyCampaignByIdResponsesStats,
    success: successGetSurveyCampaignByIdResponsesStats,
  } = getSurveyCampaignByIdResponsesStatsCall.hook();

  const { completed } = getEmployeesStatsCall.hook();

  /**
   * Current survey name
   */
  const currentSurveyName = useMemo(() => {
    if (!responseGetTemplates) return undefined;
    return responseGetTemplates.find(template => template.id === surveyId)?.name;
  }, [responseGetTemplates, surveyId]);

  /**
   * The all versions of the current survey templates
   */
  const templateItems = useMemo(() => {
    if (!responseGetTemplates) return [];
    const valueForDropdown = responseGetTemplates
      .filter(template => template.id === surveyId)
      .flatMap(
        temp =>
          temp.versions?.map(version => ({
            label: version.versionName ?? '',
            value: version.version?.toString() || '',
          })) ?? [],
      )
      .sort((a, b) => Number(b.value) - Number(a.value));
    setCurrentVersion({
      name: valueForDropdown[0].label,
      id: Number(valueForDropdown[0].value),
    });
    return valueForDropdown;
  }, [responseGetTemplates, surveyId]);

  const peopleInCampaign = useMemo(() => {
    if (!responseGetSurveysCampaigns) return [];
    return responseGetSurveysCampaigns.filter(campaign => campaign.surveyTemplateId === surveyId)[0]
      ?.invitedUserIds?.length;
  }, [responseGetSurveysCampaigns, surveyId]);

  const isVisible = useMemo(() => {
    if (!responseGetSurveysCampaigns) return false;
    return (
      responseGetSurveysCampaigns.filter(campaign => campaign.surveyTemplateId === surveyId)[0]
        ?.isVisible || false
    );
  }, [responseGetSurveysCampaigns, surveyId]);

  const currentCampaignId = useMemo(() => {
    return responseGetSurveysCampaigns?.find(
      currentCampaign =>
        currentCampaign.surveyTemplateId === surveyId &&
        currentCampaign.templateVersion === Number(currentVersion?.id),
    )?.id;
  }, [currentVersion?.id, responseGetSurveysCampaigns, surveyId]);

  const noCampaignForThisVersion = useMemo(() => {
    return !responseGetSurveysCampaigns?.find(
      currentCampaign =>
        currentCampaign.surveyTemplateId === surveyId &&
        currentCampaign.templateVersion === Number(currentVersion?.id),
    )?.id;
  }, [currentVersion?.id, responseGetSurveysCampaigns, surveyId]);

  useEffect(() => {
    if (currentCampaignId) {
      executeGetSurveyCampaignByIdResponsesStats({
        campaignId: currentCampaignId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCampaignId]);

  return {
    surveyId,
    loadingStats: loadingGetSurveyCampaignByIdResponsesStats,
    errorStats: errorGetSurveyCampaignByIdResponsesStats,
    successStats: successGetSurveyCampaignByIdResponsesStats,
    currentCampaignId,
    templateItems,
    currentSurveyName,
    peopleInCampaign,
    currentVersion,
    setCurrentVersion,
    completed,
    noCampaignForThisVersion,
    isVisible,
    currentCampaignStats: {
      completedSurveys: responseGetSurveyCampaignByIdResponsesStats?.completedSurveys || 0,
      totalCompletedEmployees:
        responseGetSurveyCampaignByIdResponsesStats?.totalCompletedEmployees || 0,
      totalInvitation: responseGetSurveyCampaignByIdResponsesStats?.totalInvitation || 0,
      lastRemindAt: responseGetSurveyCampaignByIdResponsesStats?.lastRemindAt || undefined,
    },
  };
};
